import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { StorageService, AuthService } from 'src/services';
import { Router } from '@angular/router';

@Component({
  selector: 'app-nesting',
  templateUrl: './nesting.component.html',
  styleUrls: ['./nesting.component.scss']
})
export class NestingComponent {
  private readonly isolationReloadKey = 'nestingIsolationReloaded';
  iframeSrc: SafeResourceUrl | null = null;

  loading = true;
  progress = 0;
  stageText = '正在加载核心引擎…';

  private messageHandler!: (ev: MessageEvent) => void;
  private fakeTimer: any = null;
  private finished = false;

  // ===== 可控参数 =====
  private readonly MAX_FAKE_PROGRESS = 85;      // 最多走到 85%
  private readonly MAX_FAKE_TIME = 30_000;       // 最长 60 秒
  private readonly TICK = 300;                   // 每 300ms 更新一次

  // 文案池（会轮播）
  private readonly texts = [
    '正在加载核心引擎…',
    '正在初始化运行环境…',
    '正在解析界面描述…',
    '正在加载字体和图形资源…',
    '正在准备本地数据…',
    '正在建立安全上下文…',
    '正在加载编辑器...'
  ];

  private textIndex = 0;
  private startTime = 0;

  constructor(
    private sanitizer: DomSanitizer,
    private storage: StorageService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngAfterViewInit(): void {
    if (!window.crossOriginIsolated) {
      const hasReloaded = sessionStorage.getItem(this.isolationReloadKey) === '1';
      if (!hasReloaded) {
        sessionStorage.setItem(this.isolationReloadKey, '1');
        window.location.replace('/nesting');
        return;
      }
    } else {
      sessionStorage.removeItem(this.isolationReloadKey);
    }

    this.startFakeProgress();

    this.messageHandler = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const data = ev.data;
      if (!data) return;

      if (data.type === 'qt-stage') {
        if (data.stage === 'idbfs-ready') {
          this.finishLoading();
        }
        return;
      }

      if (data.type === 'navigate') {
        this.router.navigateByUrl(data.path);
      }
    };

    window.addEventListener('message', this.messageHandler);
    void this.bootstrapIframe();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageHandler);
    clearInterval(this.fakeTimer);
  }

  // ===============================
  // 模拟进度（≤ 60 秒，≤ 85%）
  // ===============================
  private startFakeProgress(): void {
    this.progress = 0;
    this.startTime = Date.now();
    this.stageText = this.texts[0];

    this.fakeTimer = setInterval(() => {
      if (this.finished) return;

      const elapsed = Date.now() - this.startTime;
      const ratio = Math.min(elapsed / this.MAX_FAKE_TIME, 1);

      // 线性推进到 MAX_FAKE_PROGRESS
      const target = Math.floor(ratio * this.MAX_FAKE_PROGRESS);

      if (target > this.progress) {
        this.progress = target;
      }

      // 每 3 秒换一句文案
      if (elapsed / 3000 > this.textIndex + 1 && this.textIndex < this.texts.length - 1) {
        this.textIndex++;
        this.stageText = this.texts[this.textIndex];
      }

    }, this.TICK);
  }

  // ===============================
  // Qt 完成 → 收尾
  // ===============================
  private finishLoading(): void {
    if (this.finished) return;
    this.finished = true;

    clearInterval(this.fakeTimer);

    this.progress = 100;
    this.stageText = '加载完成';

    setTimeout(() => {
      this.loading = false;
    }, 300);
  }

  private async bootstrapIframe(): Promise<void> {
    let token = this.storage.getItem('wasmToken');

    if (!token && this.authService.isLoggedIn()) {
      token = await this.authService.refreshWasmToken();
    }

    console.log('wasm token', token);

    if (token) {
      this.storage.setItem('wasmToken', token);
    } else {
      this.storage.removeItem('wasmToken');
    }

    // 延后一拍再挂载 iframe，避免在当前变更检测轮次里修改资源 URL。
    setTimeout(() => {
      this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl('assets/qt/mn_ui.html');
    });
  }
}
